import { createContext, useState, useEffect, ReactNode } from 'react';
import { getProfile } from '../services/NetworkServices';
import { getTab } from '../utils/auth-token-utils/cookie';

// Define the interfaces for the profile and context
export interface NewProfile {
  firstName: string;
  lastName: string;
  phone: string;
  profilePic: string;
  idFront: string;
  idBack: string;
}

export interface UserProfile {
  [key: string]: any; // Assuming it can have dynamic keys
}

export interface Ticket {
  [key: string]: any;
}

export interface ProfileContextProps {
  showLogoutPopup: boolean;
  handleShowLogoutPopup: (value: boolean) => void;
  profileStage: number;
  handleProfileStage: (value: number) => void;
  newProfile: NewProfile;
  handleNewProfile: (updatedFields: Partial<NewProfile>) => void;
  preview: string | null;
  handlePreview: (value: string | null) => void;
  activeTab: string;
  autoRenewal: boolean;
  handleAutoRenewal: () => void;
  userProfile: UserProfile;
  updatedFrontFile: File | null;
  updatedBackFile: File | null;
  profilePicPreview: string | null;
  isProfilePicChanged: boolean;
  isGovtIdFrontChanged: boolean;
  isGovtIdBackChanged: boolean;
  ticketsList: Ticket[];
  tempProfilePic: File | null;
  isProfileCreated: boolean;
  updatedFrontPreview: string | null;
  updatedBackPreview: string | null;
  handleFrontFile: (value: File) => void;
  handleBackFile: (value: File) => void;
  handleProfileCreation: () => void;
  setTempProfilePic: (value: File | null) => void;
  handleTicketsList: (value: Ticket[]) => void;
  handleProfilePicPreview: (value: string | null) => void;
  handleProfilePic: (value: File | null) => void;
  handleProfilePicChanged: () => void;
  handleActiveTab: (value: string) => void;
  handleUserProfile: (value: UserProfile) => void;
}

// Initialize ProfileContext with undefined value
const ProfileContext = createContext<ProfileContextProps | undefined>(
  undefined,
);

interface ProfileProviderProps {
  children: ReactNode;
}

const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const tab = getTab();
  const [activeTab, setActiveTab] = useState<string>(tab ? tab : 'profile');
  const [userProfile, setUserProfile] = useState<UserProfile>({});
  const [newProfile, setNewProfile] = useState<NewProfile>({
    firstName: '',
    lastName: '',
    phone: '',
    profilePic: '',
    idFront: '',
    idBack: '',
  });

  const [autoRenewal, setAutoRenewal] = useState<boolean>(true);
  const [profilePicPreview, setProfilePicPreview] = useState<string | null>(
    null,
  );
  const [isProfilePicChanged, setIsProfilePicChanged] =
    useState<boolean>(false);
  const [isGovtIdFrontChanged, setIsGovtIdFrontChanged] =
    useState<boolean>(false);
  const [isGovtIdBackChanged, setIsGovtIdBackChanged] =
    useState<boolean>(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState<boolean>(false);

  const [profileStage, setProfileStage] = useState<number>(1);

  const [ticketsList, setTicketsList] = useState<Ticket[]>([]);
  const [tempProfilePic, setTempProfilePic] = useState<File | null>(null);
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isProfileCreated, setIsProfileCreated] = useState<boolean>(false);
  const [updatedFrontFile, setUpdatedFrontFile] = useState<File | null>(null);
  const [updatedBackFile, setUpdatedBackFile] = useState<File | null>(null);
  const [updatedFrontPreview, setupdatedFrontPreview] = useState<string | null>(
    null,
  );
  const [updatedBackPreview, setupdatedBackPreview] = useState<string | null>(
    null,
  );

  const handleNewProfile = (updatedFields: Partial<NewProfile>) => {
    console.log('updated fields are', updatedFields);
    setNewProfile((prevData) => ({
      ...prevData,
      ...updatedFields,
    }));
  };

  const handleShowLogoutPopup = (value: boolean) => {
    setShowLogoutPopup(value);
  };

  const handleProfileStage = (value: number) => {
    setProfileStage(value);
  };

  const handleProfileCreation = () => {
    setIsProfileCreated(true);
  };

  const handleFrontFile = (value: File) => {
    setUpdatedFrontFile(value);
    setupdatedFrontPreview(URL.createObjectURL(value));
    setIsGovtIdFrontChanged(true);
  };

  const handleBackFile = (value: File) => {
    setUpdatedBackFile(value);
    setupdatedBackPreview(URL.createObjectURL(value));
    setIsGovtIdBackChanged(true);
  };

  const handleAutoRenewal = () => {
    setAutoRenewal((autoRenewal) => !autoRenewal);
  };

  const handlePreview = (value: string | null) => {
    setPreview(value);
  };

  const handleTicketsList = (value: Ticket[]) => {
    setTicketsList(value);
  };

  const handleProfilePicChanged = () => {
    setIsProfilePicChanged(true);
  };

  const handleProfilePicPreview = (value: string | null) => {
    setProfilePicPreview(value);
  };

  const handleUserProfile = (value: UserProfile) => {
    setUserProfile(value);
  };

  const handleActiveTab = (value: string) => {
    setActiveTab(value);
  };

  const handleProfilePic = (value: File | null) => {
    setProfilePic(value);
  };

  useEffect(() => {
    const fetchProfileDetails = async () => {
      try {
        const currentPath = window.location.pathname;
        if (
          currentPath === '/signin' ||
          currentPath === '/' ||
          currentPath === '/signup'
        ) {
          return;
        }
        // console.log("inside use effect");
        const response = await getProfile();
        const data = await response.data;
        if (data) {
          // console.log("data type  is", typeof data.data.userProfile.phone);
          // console.log("data is", data.data.userProfile.phone);
          setUserProfile(data.data.userProfile);
          // console.log("stage is", data.data.userProfile.stage);
        }
      } catch (error) {
        // console.error("Error fetching profile details:", error);
      }
    };
    fetchProfileDetails();
  }, [isProfileCreated]);

  return (
    <ProfileContext.Provider
      value={{
        handleShowLogoutPopup,
        showLogoutPopup,
        profileStage,
        handleProfileStage,
        newProfile,
        handleNewProfile,
        preview,
        handlePreview,
        activeTab,
        autoRenewal,
        handleAutoRenewal,
        userProfile,
        updatedFrontFile,
        updatedBackFile,
        profilePicPreview,
        isProfilePicChanged,
        isGovtIdFrontChanged,
        isGovtIdBackChanged,
        ticketsList,
        tempProfilePic,
        isProfileCreated,
        updatedFrontPreview,
        updatedBackPreview,
        handleFrontFile,
        handleBackFile,
        handleProfileCreation,
        setTempProfilePic,
        handleTicketsList,
        handleProfilePicPreview,
        handleProfilePic,
        handleProfilePicChanged,
        handleActiveTab,
        handleUserProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export { ProfileContext, ProfileProvider };
